// Two breakdown panels under the chart strip:
//   1. Sub-agent rollup — top 10 by turn_count, with cost + grounding +
//      latency. Click a row to add the sub-agent to the filter set.
//   2. Route-class rollup — same drill-through, smaller list.
//
// We render these as a styled HTML grid rather than a chart because the
// rows already carry several metrics each — a bar chart would only show
// one dimension and the rest would live in a tooltip.

import type { ActivityUiState } from "@/hooks/useActivityFilters";
import type { ActivityStatsResponse } from "@/services/api/me";
import { cn } from "@/utils/cn";

function maxCount(rows: { turn_count: number }[]): number {
  return rows.reduce((m, r) => Math.max(m, r.turn_count), 0) || 1;
}

interface SubAgentTableProps {
  rows: ActivityStatsResponse["by_sub_agent"];
  state: ActivityUiState;
}

function SubAgentTable({ rows, state }: SubAgentTableProps) {
  if (rows.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm italic text-warmgray-400">
        No sub-agent activity in this window.
      </p>
    );
  }
  const peak = maxCount(rows);
  const activeAgents = new Set(state.filters.subAgents ?? []);
  return (
    <ul className="divide-y divide-ruleline">
      {rows.map((r) => {
        const isActive = activeAgents.has(r.sub_agent);
        const widthPct = Math.max(4, Math.round((r.turn_count / peak) * 100));
        return (
          <li key={r.sub_agent}>
            <button
              type="button"
              onClick={() => state.toggleSubAgent(r.sub_agent)}
              className={cn(
                "block w-full px-3 py-2 text-left transition-colors",
                isActive ? "bg-parchment-200" : "hover:bg-parchment-50",
              )}
              title="Toggle filter"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span
                  className={cn(
                    "font-mono text-xs",
                    isActive ? "text-oxblood-500" : "text-espresso-700",
                  )}
                >
                  {r.sub_agent}
                </span>
                <span className="font-mono text-[0.7rem] text-warmgray-500">
                  {r.turn_count} · ${r.cost_usd}
                  {r.median_grounding != null && (
                    <span className="ml-2 text-warmgray-400">
                      g {r.median_grounding.toFixed(2)}
                    </span>
                  )}
                  {r.median_latency_ms != null && (
                    <span className="ml-2 text-warmgray-400">{r.median_latency_ms} ms</span>
                  )}
                </span>
              </div>
              <div className="mt-1 h-1 rounded bg-parchment-100">
                <div
                  className={cn("h-full rounded", isActive ? "bg-oxblood-500" : "bg-warmgray-400")}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

interface RouteTableProps {
  rows: ActivityStatsResponse["by_route_class"];
  state: ActivityUiState;
}

function RouteTable({ rows, state }: RouteTableProps) {
  if (rows.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm italic text-warmgray-400">
        No traffic in this window.
      </p>
    );
  }
  const peak = maxCount(rows);
  const activeRoutes = new Set(state.filters.routeClasses ?? []);
  return (
    <ul className="divide-y divide-ruleline">
      {rows.map((r) => {
        const isActive = activeRoutes.has(r.route_class);
        const widthPct = Math.max(4, Math.round((r.turn_count / peak) * 100));
        const isKnown = ["lookup", "analysis", "deep_work"].includes(r.route_class);
        return (
          <li key={r.route_class}>
            <button
              type="button"
              onClick={() => isKnown && state.toggleRouteClass(r.route_class)}
              disabled={!isKnown}
              className={cn(
                "block w-full px-3 py-2 text-left transition-colors",
                !isKnown && "cursor-default",
                isActive ? "bg-parchment-200" : isKnown ? "hover:bg-parchment-50" : "",
              )}
              title={isKnown ? "Toggle filter" : "Unfilterable bucket"}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span
                  className={cn(
                    "font-mono text-xs",
                    isActive ? "text-oxblood-500" : "text-espresso-700",
                  )}
                >
                  {r.route_class}
                </span>
                <span className="font-mono text-[0.7rem] text-warmgray-500">
                  {r.turn_count} · ${r.cost_usd}
                </span>
              </div>
              <div className="mt-1 h-1 rounded bg-parchment-100">
                <div
                  className={cn("h-full rounded", isActive ? "bg-oxblood-500" : "bg-warmgray-400")}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function ActivityBreakdowns({
  state,
  stats,
  isLoading,
}: {
  state: ActivityUiState;
  stats: ActivityStatsResponse | undefined;
  isLoading: boolean;
}) {
  return (
    <section className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
      <div className="rounded border border-ruleline bg-parchment-50 md:col-span-2">
        <header className="border-b border-ruleline px-3 py-2">
          <p className="smallcaps text-oxblood-500">Sub-agents</p>
          <p className="font-mono text-[0.7rem] text-warmgray-500">
            Click a row to filter the list below
          </p>
        </header>
        {isLoading && !stats ? (
          <p className="px-3 py-6 text-center text-sm italic text-warmgray-400">Loading…</p>
        ) : (
          <SubAgentTable rows={stats?.by_sub_agent ?? []} state={state} />
        )}
      </div>

      <div className="rounded border border-ruleline bg-parchment-50">
        <header className="border-b border-ruleline px-3 py-2">
          <p className="smallcaps text-oxblood-500">Route class</p>
          <p className="font-mono text-[0.7rem] text-warmgray-500">Click to filter</p>
        </header>
        {isLoading && !stats ? (
          <p className="px-3 py-6 text-center text-sm italic text-warmgray-400">Loading…</p>
        ) : (
          <RouteTable rows={stats?.by_route_class ?? []} state={state} />
        )}
      </div>
    </section>
  );
}
